import React from "react";
import { format, parseISO, subDays } from "date-fns";
import { HeartPulse } from "lucide-react";

import { Card, CardContent } from "../../components/misc/Card";
import Spinner from "../../components/misc/Spinner";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import { cn } from "../../utils/classMerger";
import {
    AccountHealthRow,
    AccountHealthTier,
    AccountHealthTrailPoint,
    TIER_DOT,
    TIER_LABELS,
    formatEastern,
    formatEasternDay,
} from "./types";

interface AccountHealthListProps {
    rows: AccountHealthRow[];
    // The night the rows belong to; the trail counts back from it.
    day: string | null;
    loading: boolean;
    expanded: Set<string>;
    onToggle: (userId: string) => void;
    emptyMessage: string;
    className?: string;
}

const COLUMN_COUNT = 7;
const TRAIL_NIGHTS = 14;

const TIER_PILL: Record<AccountHealthTier, string> = {
    "at-risk": "bg-[#ff5252]/20 text-[#b9121b]",
    watch: "bg-lightSecondary text-primary",
    healthy: "bg-[#beeb9f] text-primary",
};

const TierPill: React.FC<{ tier: AccountHealthTier }> = ({ tier }) => (
    <span
        className={cn(
            "inline-flex items-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold",
            TIER_PILL[tier],
        )}
    >
        {TIER_LABELS[tier]}
    </span>
);

// One dot per night, oldest first, ending on the night shown; grey = no snapshot.
// Days are parsed in local time on purpose: whole-day steps never cross a DST change.
const Trail: React.FC<{ day: string; trail: AccountHealthTrailPoint[] }> = ({ day, trail }) => {
    const byDay = new Map(trail.map((p) => [p.day, p.tier]));
    const end = parseISO(day);
    const nights = Array.from({ length: TRAIL_NIGHTS }, (_, i) =>
        format(subDays(end, TRAIL_NIGHTS - 1 - i), "yyyy-MM-dd"),
    );
    const nightsIn = (tier?: AccountHealthTier): number => nights.filter((n) => byDay.get(n) === tier).length;
    const summary = `Last ${TRAIL_NIGHTS} nights: ${nightsIn("at-risk")} At Risk, ${nightsIn("watch")} Watch, ${nightsIn(
        "healthy",
    )} Healthy, ${nightsIn(undefined)} no snapshot`;
    return (
        <div className="flex items-center gap-[3px]" role="img" aria-label={summary}>
            {nights.map((n) => {
                const tier = byDay.get(n);
                return (
                    <span
                        key={n}
                        data-testid="trail-dot"
                        title={`${n} · ${tier ? TIER_LABELS[tier] : "no snapshot"}`}
                        className={cn("inline-block h-2.5 w-2.5 rounded-full", tier ? TIER_DOT[tier] : "bg-gray-200")}
                    />
                );
            })}
        </div>
    );
};

const sinceLabel = (row: AccountHealthRow): string => format(parseISO(row.since), "MMM d");

// previousTier is the tier of the latest earlier snapshot, so for an account
// that stayed put it equals the current tier: say "was X" only on a move.
const moveLabel = (row: AccountHealthRow): string | null =>
    row.previousTier === null
        ? "(first check)"
        : row.previousTier !== row.tier
          ? `(was ${TIER_LABELS[row.previousTier]})`
          : null;

// Everything the alert email says that the row does not: the move, the notes,
// the upgrade conversation, the play, and whether an email went out.
const Details: React.FC<{ row: AccountHealthRow }> = ({ row }) => (
    <div className="grid gap-2 text-xs text-gray-700">
        <div>
            <span className="font-semibold">{TIER_LABELS[row.tier]}</span> since {sinceLabel(row)}
            {moveLabel(row) ? ` ${moveLabel(row)}` : ""}
            {" · "}
            {row.alertSentAt ? `Alert sent ${formatEastern(row.alertSentAt)}` : "No alert sent for this night"}
        </div>
        {row.notes.length > 0 && (
            <div>
                <span className="font-semibold">Also:</span> {row.notes.join(" ")}
            </div>
        )}
        {row.opportunities.length > 0 && (
            <div>
                <span className="font-semibold">Upgrade conversation:</span> {row.opportunities.join(" ")}
            </div>
        )}
        {row.suggestedPlay && (
            <div>
                <span className="font-semibold">Suggested play:</span> {row.suggestedPlay}
            </div>
        )}
        <div className="text-gray-500">
            {[row.email, row.plan, row.renewsAt ? `renews ${formatEasternDay(row.renewsAt)}` : null]
                .filter(Boolean)
                .join(" · ")}
        </div>
    </div>
);

const AccountHealthList: React.FC<AccountHealthListProps> = ({
    rows,
    day,
    loading,
    expanded,
    onToggle,
    emptyMessage,
    className,
}) => (
    <Card className={cn("mt-0", className)}>
        <CardContent className="p-4">
            {loading ? (
                <div className="flex justify-center py-10">
                    <Spinner />
                </div>
            ) : rows.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-gray-500">
                    <HeartPulse className="h-8 w-8" />
                    <span className="text-sm">{emptyMessage}</span>
                </div>
            ) : (
                <Table className="min-w-[1100px]">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[220px]">Customer</TableHead>
                            <TableHead className="w-[170px]">Plan</TableHead>
                            <TableHead className="w-[100px]">Tier</TableHead>
                            <TableHead className="w-[90px]">Since</TableHead>
                            <TableHead className="w-[70px]">Score</TableHead>
                            <TableHead>Reasons</TableHead>
                            <TableHead className="w-[190px]">Last 14 nights</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((r) => {
                            const open = expanded.has(r.userId);
                            return (
                                <React.Fragment key={r.userId}>
                                    <TableRow
                                        data-testid={`row-${r.userId}`}
                                        tabIndex={0}
                                        aria-expanded={open}
                                        onClick={() => {
                                            // Dragging to copy an email must not toggle the row.
                                            if (window.getSelection()?.toString()) return;
                                            onToggle(r.userId);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                onToggle(r.userId);
                                            }
                                        }}
                                        className="cursor-pointer focus:outline-none focus-visible:bg-gray-50"
                                    >
                                        <TableCell className="max-w-0 align-top text-xs">
                                            <div className="truncate font-semibold text-primary">{r.name}</div>
                                            {r.accountName && (
                                                <div className="truncate text-gray-700">{r.accountName}</div>
                                            )}
                                            <div className="truncate text-gray-500">{r.email}</div>
                                        </TableCell>
                                        <TableCell className="align-top text-xs">{r.plan ?? "-"}</TableCell>
                                        <TableCell className="align-top">
                                            <TierPill tier={r.tier} />
                                        </TableCell>
                                        <TableCell className="align-top text-xs text-gray-500">
                                            {sinceLabel(r)}
                                        </TableCell>
                                        <TableCell className="align-top text-xs">{r.score}</TableCell>
                                        <TableCell className="min-w-[260px] max-w-[460px] whitespace-normal break-words align-top text-xs">
                                            {r.reasons.length ? (
                                                <ul className="list-disc pl-4">
                                                    {r.reasons.map((reason, i) => (
                                                        <li key={`${i}:${reason}`}>{reason}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <span className="text-gray-500">No warning fired</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="align-top">
                                            {day && <Trail day={day} trail={r.trail} />}
                                        </TableCell>
                                    </TableRow>
                                    {open && (
                                        <TableRow data-testid={`details-${r.userId}`} className="bg-gray-50">
                                            <TableCell colSpan={COLUMN_COUNT} className="px-6 py-3">
                                                <Details row={r} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </CardContent>
    </Card>
);

export default AccountHealthList;
